import type { DocumentNode, DataWithId } from '@trello/graphql';
import { MemberBoardsDocument } from './queries/MemberBoardsQuery.generated';
import { MemberAllBoardsDocument } from './queries/MemberAllBoardsQuery.generated';
import { MemberQuickBoardsDocument } from './queries/MemberQuickBoardsQuery.generated';
import { QuickBoardsSearchDocument } from './queries/QuickBoardsSearchQuery.generated';
import { CurrentBoardFullDocument } from './queries/CurrentBoardFullQuery.generated';
import { PreloadCardDocument } from './queries/PreloadCardQuery.generated';
import { WorkspaceBoardsPageMinimalDocument } from './queries/WorkspaceBoardsPageMinimalQuery.generated';
import { Routes } from './Routes';

export interface JSONWithId extends JSON {
  id: string;
}

export const preloadQueryHash: Record<
  Routes,
  {
    queries: {
      query: DocumentNode;
      modelName: string;
      getVariables: (data: DataWithId) => object;
    }[];
  }
> = {
  // we've come directly to trello.com, so load the data for the member boards page
  [Routes.MemberHome]: {
    queries: [
      {
        query: MemberBoardsDocument,
        modelName: 'Member',
        getVariables: (memberBoardData) => ({ id: memberBoardData.id }),
      },
    ],
  },
  // we've come to /username/boards
  [Routes.MemberAllBoards]: {
    queries: [
      {
        query: MemberAllBoardsDocument,
        modelName: 'Member',
        getVariables: (memberBoardData) => ({ id: memberBoardData.id }),
      },
    ],
  },
  // we're performing a search using '//', but there was no search term
  [Routes.EmptySearch]: {
    queries: [
      {
        query: MemberQuickBoardsDocument,
        modelName: 'Member',
        getVariables: (quickBoardData) => ({ id: quickBoardData.id }),
      },
    ],
  },
  // we're performing a search using '//'
  [Routes.Search]: {
    queries: [
      {
        query: MemberQuickBoardsDocument,
        modelName: 'Member',
        getVariables: (quickBoardData) => ({ id: quickBoardData.id }),
      },
      {
        query: QuickBoardsSearchDocument,
        modelName: 'Member',
        getVariables: (quickBoardSearch) => ({ id: quickBoardSearch.id }),
      },
    ],
  },
  // we're on a board route
  [Routes.Board]: {
    queries: [
      {
        query: CurrentBoardFullDocument,
        modelName: 'Board',
        getVariables: (board) => ({ id: board.id }),
      },
    ],
  },
  // we're on the card route
  [Routes.Card]: {
    queries: [
      {
        query: PreloadCardDocument,
        modelName: 'Card',
        getVariables: (cardData) => ({ id: cardData.id }),
      },
    ],
  },
  // we might be on an organization
  [Routes.Organization]: {
    queries: [
      {
        query: WorkspaceBoardsPageMinimalDocument,
        modelName: 'Organization',
        getVariables: (workspaceData) => ({ id: workspaceData.id }),
      },
    ],
  },
};
